import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { LogOut, ArrowLeft } from 'lucide-react';
import { useDialog } from '../ui/DialogProvider.js';
import { ExpoMobileSimulator } from '../expo/ExpoMobileSimulator.js';

export const PortalDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { showAlert } = useDialog();
  
  // Data States
  const [student, setStudent] = useState<any>(null);
  const [tagihanList, setTagihanList] = useState<any[]>([]);
  const [transaksiList, setTransaksiList] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  const loadData = async () => {
    try {
      setLoading(true);
      const siswaId = localStorage.getItem('portal_siswa_id');
      if (!siswaId) {
        navigate('../login', { replace: true });
        return;
      }

      const token = localStorage.getItem('portal_token');
      const headers = {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      };

      const [resSiswa, resTagihan, resTrx] = await Promise.all([
        fetch('/api/students', { headers }),
        fetch('/api/billing/tagihan', { headers }),
        fetch('/api/payment', { headers })
      ]);

      if (!resSiswa.ok || !resTagihan.ok || !resTrx.ok) {
        if (resSiswa.status === 401 || resTagihan.status === 401 || resTrx.status === 401) {
          localStorage.removeItem('portal_token');
          localStorage.removeItem('portal_siswa_id');
          navigate('../login', { replace: true });
          return;
        }
        throw new Error('Server error: one or more API calls failed');
      }

      const [dataSiswa, dataTagihan, dataTrx] = await Promise.all([
        resSiswa.json(), resTagihan.json(), resTrx.json()
      ]);

      if (dataSiswa.success && dataSiswa.data) {
        const s = Array.isArray(dataSiswa.data)
          ? (dataSiswa.data.find((x: any) => x.id === siswaId || x.nis === siswaId) || dataSiswa.data[0])
          : dataSiswa.data;
        if (s) {
          setStudent(s);
          if (dataTagihan.success) {
            setTagihanList((dataTagihan.data || []).filter((t: any) => t.siswaId === s.id || t.nis === s.nis));
          }
          if (dataTrx.success) {
            setTransaksiList((dataTrx.data || []).filter((t: any) => t.siswaId === s.id || t.nis === s.nis));
          }
        } else {
          throw new Error('Student data not found in response');
        }
      } else {
        localStorage.removeItem('portal_token');
        localStorage.removeItem('portal_siswa_id');
        navigate('../login', { replace: true });
        return;
      }
    } catch (err: any) {
      console.error('Portal loadData error:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('portal_token');
    localStorage.removeItem('portal_siswa_id');
    navigate('../login', { replace: true });
  };

  const handleProcessPayment = async (payload: any) => {
    if (payload?.reloadOnly) {
      await loadData();
      return { success: true };
    }
    const token = localStorage.getItem('portal_token');
    const res = await fetch('/api/payment/process', {
      method: 'POST',
      headers: { 
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(payload)
    });
    const json = await res.json();
    if (!json.success) throw new Error(json.message);
    
    // Refresh data setelah berhasil
    await loadData();
    return json;
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-full gap-3 text-slate-500">
        <div className="w-8 h-8 border-4 border-sky-600 border-t-transparent rounded-full animate-spin"></div>
        <p className="text-xs font-semibold">Memuat data tagihan...</p>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col relative">
      {/* Tombol Logout tersembunyi di atas layar (hanya untuk portal web) */}
      <div className="absolute top-2 right-2 z-[60]">
        <button 
          onClick={handleLogout}
          className="p-2 bg-rose-500/90 hover:bg-rose-600 text-white rounded-full shadow-lg backdrop-blur transition"
          title="Keluar"
        >
          <LogOut className="w-4 h-4" />
        </button>
      </div>

      <div className="flex-1 w-full h-full -mt-4">
        {student && (
          <ExpoMobileSimulator 
            siswaList={[student]} 
            tagihanList={tagihanList} 
            transaksiList={transaksiList}
            onProcessPayment={handleProcessPayment}
            isRealUser={true}
          />
        )}
      </div>
    </div>
  );
};
